import { useState, useEffect } from "react";
import { formatStamp } from "../lib/richText";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getProjects, updateProjectStatus, sendDailySummary, updateProjectLifecycle, deleteProject } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { FolderKanban, Loader2, Search, MoreHorizontal, Eye, ShoppingCart, LayoutGrid, List, Mail, X, Repeat, Archive, Trash2, Sparkles } from "lucide-react";
import OperationsBoard from "../components/OperationsBoard";

const PRODUCT_LABELS = {
  marketing: "Marketing", web_design: "Web Design", seo_aeo: "SEO/AEO",
  prospect_tools: "ProspectID/Max", digital_marketing: "Digital Marketing", ai_tools: "AI Tools",
};

export default function ActiveOrdersPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilter = searchParams.get("category") || "";
  const verificationFilter = searchParams.get("verification") || ""; // "pending" filters to unverified
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // "Last viewed" timestamp — orders created after this show a NEW badge.
  // Snapshot on mount so the badge persists for the whole session; bumped to "now"
  // when the user navigates away so next visit only badges newer orders.
  const lastViewedStorageKey = `orders_last_viewed_${user?.id || "anon"}`;
  const [lastViewedAt] = useState(() => {
    try {
      const stored = localStorage.getItem(lastViewedStorageKey);
      // Default: 24 hours ago for first-time visitors
      return stored ? parseInt(stored, 10) : Date.now() - 24 * 60 * 60 * 1000;
    } catch {
      return Date.now() - 24 * 60 * 60 * 1000;
    }
  });

  useEffect(() => {
    // Bump "last viewed" when the user leaves the page
    return () => {
      try { localStorage.setItem(lastViewedStorageKey, String(Date.now())); } catch { /* ignore */ }
    };
  }, [lastViewedStorageKey]);

  const fetchProjects = async () => {
    try {
      const { data } = await getProjects();
      // Filter to onboarding-stage orders, then sort newest-first
      const filtered = data
        .filter((p) => ["pending", "in_progress", "on_hold"].includes(p.status) && (p.lifecycle_stage || "onboarding") === "onboarding")
        .sort((a, b) => {
          const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
          const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
          return tb - ta;
        });
      setProjects(filtered);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const isAdmin = ["super_admin", "manager"].includes(user?.role);

  const [lifecyclePromptOpen, setLifecyclePromptOpen] = useState(false);
  const [pendingCompletedId, setPendingCompletedId] = useState(null);

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await updateProjectStatus(projectId, newStatus);
      await fetchProjects();
      if (newStatus === "completed" && isAdmin) {
        setPendingCompletedId(projectId);
        setLifecyclePromptOpen(true);
      }
    } catch (e) { console.error(e); }
  };

  const handleLifecycleChange = async (projectId, stage) => {
    try {
      await updateProjectLifecycle(projectId, stage);
      toast.success(stage === "monthly_active" ? "Moved to Monthly Active Clients" : "Moved to Archived Clients");
      setLifecyclePromptOpen(false);
      setPendingCompletedId(null);
      fetchProjects();
    } catch (e) { toast.error("Failed to update lifecycle stage"); }
  };

  const handleDeleteOrder = async (projectId, clientLabel) => {
    if (!window.confirm(`Permanently delete this order for ${clientLabel || "this client"}? Related tasks and comments will also be removed. This cannot be undone.`)) return;
    try {
      await deleteProject(projectId);
      toast.success("Order deleted");
      fetchProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete order");
    }
  };

  const getStatusBadgeClass = (status) => {
    const c = { pending: "status-pending", in_progress: "status-in_progress", on_hold: "status-on_hold" };
    return c[status] || "status-pending";
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const getProductLabel = (p) => (p.product_types || [p.product_type]).map((t) => PRODUCT_LABELS[t] || t).join(" + ");

  const clearCategory = () => {
    const np = new URLSearchParams(searchParams);
    np.delete("category");
    setSearchParams(np);
  };

  const filtered = projects.filter((p) => {
    // Category filter
    if (categoryFilter) {
      const pts = p.product_types || (p.product_type ? [p.product_type] : []);
      if (!pts.includes(categoryFilter)) return false;
    }
    // Verification filter ("pending" = only unverified)
    if (verificationFilter === "pending" && p.verified !== false) return false;
    // Search filter
    return (
      p.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.client_company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getProductLabel(p).toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Group by client — preserve newest-first ordering by inserting in projects' sorted order.
  // We also track the newest order timestamp per group to sort groups themselves.
  const groupedByClient = {};
  filtered.forEach((p) => {
    const key = p.client_name || "Unknown";
    if (!groupedByClient[key]) {
      groupedByClient[key] = {
        client_company: p.client_company,
        agency_name: p.agency_name || "",
        orders: [],
        newest_created_at: p.created_at,
      };
    }
    groupedByClient[key].orders.push(p);
    const a = p.created_at ? new Date(p.created_at).getTime() : 0;
    const b = groupedByClient[key].newest_created_at ? new Date(groupedByClient[key].newest_created_at).getTime() : 0;
    if (a > b) groupedByClient[key].newest_created_at = p.created_at;
  });
  // Re-create as an ordered object: newest group first
  const orderedGroupedByClient = Object.fromEntries(
    Object.entries(groupedByClient).sort(([, ga], [, gb]) => {
      const ta = ga.newest_created_at ? new Date(ga.newest_created_at).getTime() : 0;
      const tb = gb.newest_created_at ? new Date(gb.newest_created_at).getTime() : 0;
      return tb - ta;
    })
  );

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const handleSendSummary = async () => {
    try {
      const { data } = await sendDailySummary();
      toast.success(data.message);
    } catch (e) {
      toast.error("Failed to send summary");
    }
  };

  return (
    <div className="space-y-6" data-testid="active-orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight">New Client Onboarding</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
            <span>{filtered.length} new orders in onboarding</span>
            {categoryFilter && (
              <Badge variant="outline" className="gap-1 cursor-pointer" onClick={clearCategory} data-testid="clear-category-filter">
                {PRODUCT_LABELS[categoryFilter] || categoryFilter} <X className="h-3 w-3" />
              </Badge>
            )}
            {verificationFilter === "pending" && (
              <Badge
                variant="outline"
                className="gap-1 cursor-pointer border-amber-300 bg-amber-50 text-amber-800"
                onClick={() => {
                  const np = new URLSearchParams(searchParams);
                  np.delete("verification");
                  setSearchParams(np);
                }}
                data-testid="clear-verification-filter"
              >
                Pending Verification <X className="h-3 w-3" />
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "super_admin" && (
            <Button variant="outline" size="sm" onClick={handleSendSummary} data-testid="send-summary-btn">
              <Mail className="mr-2 h-4 w-4" />Send Daily Summary
            </Button>
          )}
          {["manager", "rep"].includes(user?.role) && (
            <Link to="/orders/new"><Button data-testid="new-order-button"><ShoppingCart className="mr-2 h-4 w-4" />Book New Order</Button></Link>
          )}
        </div>
      </div>

      {isAdmin ? (
        <Tabs defaultValue="orders">
          <TabsList>
            <TabsTrigger value="orders" data-testid="tab-orders-list">
              <List className="h-4 w-4 mr-2" />Orders List
            </TabsTrigger>
            <TabsTrigger value="operations" data-testid="tab-operations">
              <LayoutGrid className="h-4 w-4 mr-2" />Operations Board
            </TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-4">
            <OrdersList
              projects={filtered}
              groupedByClient={orderedGroupedByClient}
              user={user}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handleStatusChange={handleStatusChange}
              handleLifecycleChange={handleLifecycleChange}
              handleDeleteOrder={handleDeleteOrder}
              getStatusBadgeClass={getStatusBadgeClass}
              formatDate={formatDate}
              lastViewedAt={lastViewedAt}
            />
          </TabsContent>

          <TabsContent value="operations" className="mt-4">
            <OperationsBoard />
          </TabsContent>
        </Tabs>
      ) : (
        <OrdersList
          projects={filtered}
          groupedByClient={orderedGroupedByClient}
          user={user}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleStatusChange={handleStatusChange}
          handleLifecycleChange={handleLifecycleChange}
          handleDeleteOrder={handleDeleteOrder}
          getStatusBadgeClass={getStatusBadgeClass}
          formatDate={formatDate}
          lastViewedAt={lastViewedAt}
        />
      )}

      {/* Lifecycle prompt after marking completed */}
      <Dialog open={lifecyclePromptOpen} onOpenChange={setLifecyclePromptOpen}>
        <DialogContent className="max-w-md" data-testid="lifecycle-prompt-dialog">
          <DialogHeader>
            <DialogTitle>Order Completed — Where does it go next?</DialogTitle>
            <DialogDescription>Pick the right bucket for this client.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <button
              type="button"
              onClick={() => handleLifecycleChange(pendingCompletedId, "monthly_active")}
              data-testid="move-to-monthly-active"
              className="text-left p-4 border-2 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition-all"
            >
              <div className="flex items-center gap-2 font-semibold text-emerald-800 mb-1">
                <Repeat className="h-5 w-5" /> Monthly Active Client
              </div>
              <p className="text-sm text-muted-foreground ml-7">
                Recurring monthly work — SEO, content, marketing campaigns. Reps can submit change requests.
              </p>
            </button>
            <button
              type="button"
              onClick={() => handleLifecycleChange(pendingCompletedId, "archived")}
              data-testid="move-to-archived"
              className="text-left p-4 border-2 rounded-lg hover:border-zinc-400 hover:bg-zinc-50 transition-all"
            >
              <div className="flex items-center gap-2 font-semibold text-zinc-800 mb-1">
                <Archive className="h-5 w-5" /> Archived Client
              </div>
              <p className="text-sm text-muted-foreground ml-7">
                One-time project complete (e.g., website build). Reps can re-order a new campaign anytime.
              </p>
            </button>
          </div>
          <Button variant="ghost" onClick={() => setLifecyclePromptOpen(false)} className="w-full" data-testid="lifecycle-prompt-skip">Decide later</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrdersList({ projects, groupedByClient, user, searchQuery, setSearchQuery, handleStatusChange, handleLifecycleChange, handleDeleteOrder, getStatusBadgeClass, formatDate, lastViewedAt }) {
  const isAdmin = ["super_admin", "manager"].includes(user?.role);
  const isNewOrder = (order) => {
    if (!order.created_at || !lastViewedAt) return false;
    return new Date(order.created_at).getTime() > lastViewedAt;
  };
  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search by client name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-orders-input" />
      </div>

      {projects.length === 0 ? (
        <Card className="border"><CardContent className="flex flex-col items-center justify-center py-16">
          <FolderKanban className="h-16 w-16 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">No active orders</h3>
          <p className="text-muted-foreground text-sm mb-4">Book a new order to get started</p>
          <Link to="/orders/new"><Button>Book New Order</Button></Link>
        </CardContent></Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedByClient).map(([clientName, group]) => (
            <Card key={clientName} className="border">
              <CardHeader className="pb-3">
                <CardTitle className="font-outfit text-lg flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-semibold">
                    {clientName.charAt(0).toUpperCase()}
                  </div>
                  {clientName}
                  {group.agency_name && <span className="text-sm font-normal text-muted-foreground">/ {group.agency_name}</span>}
                  {group.client_company && <span className="text-xs font-normal text-muted-foreground ml-1">({group.client_company})</span>}
                  <Badge variant="outline" className="ml-auto">{group.orders.length} order{group.orders.length > 1 ? "s" : ""}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {group.orders.map((order) => {
                  const done = order.tasks?.filter((t) => t.status === "completed").length || 0;
                  const total = order.tasks?.length || 0;
                  const items = order.sub_product_names?.length
                    ? order.sub_product_names
                    : (order.sub_products || []).map((s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));
                  const newOrder = isNewOrder(order);
                  return (
                    <div
                      key={order.id}
                      className={`rounded-md border p-4 hover:bg-secondary/20 transition-colors ${newOrder ? "border-emerald-400 bg-emerald-50/40 ring-1 ring-emerald-200" : "hover:border-primary/40"}`}
                      data-testid={`order-card-${order.id}`}
                    >
                      <div className="flex flex-wrap items-start gap-3 justify-between">
                        <div className="flex-1 min-w-0 space-y-2">
                          {/* Products + Items */}
                          <div className="flex flex-wrap items-center gap-1.5">
                            {newOrder && (
                              <Badge className="bg-emerald-600 text-white border-0 text-xs animate-pulse" data-testid={`new-order-badge-${order.id}`}>
                                <Sparkles className="h-3 w-3 mr-1" /> NEW
                              </Badge>
                            )}
                            {(order.product_types || [order.product_type]).map((t) => (
                              <Badge key={t} className="text-xs">{PRODUCT_LABELS[t] || t}</Badge>
                            ))}
                            {items.slice(0, 4).map((it, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{it}</Badge>
                            ))}
                            {items.length > 4 && (
                              <Badge variant="outline" className="text-xs">+{items.length - 4} more</Badge>
                            )}
                            <Badge className={`ml-1 ${getStatusBadgeClass(order.status)}`}>{order.status.replace("_", " ")}</Badge>
                          </div>

                          {/* Inline details grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1.5 text-sm">
                            <div>
                              <span className="text-muted-foreground text-xs block">Submitted</span>
                              <span className="font-medium" data-testid={`order-submitted-${order.id}`}>{formatStamp(order.created_at)}</span>
                            </div>
                            {order.start_date && (
                              <div>
                                <span className="text-muted-foreground text-xs block">Starts</span>
                                <span className="font-medium">{order.start_date}{order.auto_renew ? " · Auto-Renew" : (order.end_date ? ` → ${order.end_date}` : "")}</span>
                              </div>
                            )}
                            {order.contract_value != null && (
                              <div>
                                <span className="text-muted-foreground text-xs block">{order.auto_renew ? "Monthly" : "Contract"}</span>
                                <span className="font-medium">
                                  ${Number(order.contract_value).toLocaleString()}
                                  {order.contract_value_type && (
                                    <span className={`ml-1 text-xs font-semibold ${order.contract_value_type === "retail" ? "text-amber-700" : "text-blue-700"}`}>
                                      ({order.contract_value_type === "retail" ? "Retail" : "Wholesale"})
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}
                            {order.geo_target && (
                              <div>
                                <span className="text-muted-foreground text-xs block">Geo</span>
                                <span className="font-medium truncate block" title={order.geo_target}>{order.geo_target}</span>
                              </div>
                            )}
                            {user?.role !== "rep" && order.rep_name && (
                              <div>
                                <span className="text-muted-foreground text-xs block">Rep</span>
                                <span className="font-medium">{order.rep_name}</span>
                              </div>
                            )}
                            {order.ad_creative_source && (
                              <div>
                                <span className="text-muted-foreground text-xs block">Ad Creative</span>
                                <span className="font-medium">{order.ad_creative_source === "attached" ? "Attached" : order.ad_creative_source === "to_come" ? "To Come" : order.ad_creative_source === "rp_builds" ? "RP to Build" : order.ad_creative_source}</span>
                              </div>
                            )}
                            {order.hosting_option && (
                              <div>
                                <span className="text-muted-foreground text-xs block">Hosting</span>
                                <span className="font-medium">{order.hosting_option === "hosting_only" ? "Hosting Only" : "Hosting + Maintenance"}</span>
                              </div>
                            )}
                            {order.use_prospect_id && (
                              <div>
                                <span className="text-muted-foreground text-xs block">ProspectID</span>
                                <Badge className="bg-primary/10 text-primary border-0 text-xs">Enabled</Badge>
                              </div>
                            )}
                            <div>
                              <span className="text-muted-foreground text-xs block">Tasks</span>
                              <span className="font-medium">{done}/{total} done</span>
                            </div>
                          </div>
                        </div>

                        {/* Right-side actions: prominent View Order button + overflow menu */}
                        <div className="flex items-center gap-2 shrink-0">
                          <Button asChild size="sm" data-testid={`view-order-btn-${order.id}`}>
                            <Link to={`/orders/${order.id}`}>
                              <Eye className="h-4 w-4 mr-1.5" /> View Order
                            </Link>
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`order-actions-${order.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {order.status !== "in_progress" && <DropdownMenuItem onClick={() => handleStatusChange(order.id, "in_progress")}>Mark In Progress</DropdownMenuItem>}
                              {order.status !== "completed" && <DropdownMenuItem onClick={() => handleStatusChange(order.id, "completed")}>Mark Completed</DropdownMenuItem>}
                              {order.status !== "on_hold" && <DropdownMenuItem onClick={() => handleStatusChange(order.id, "on_hold")}>Put On Hold</DropdownMenuItem>}
                              {isAdmin && (
                                <>
                                  <DropdownMenuItem onClick={() => handleLifecycleChange(order.id, "monthly_active")} data-testid={`promote-monthly-${order.id}`}>
                                    <Repeat className="mr-2 h-4 w-4" />Move to Monthly Active
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleLifecycleChange(order.id, "archived")} data-testid={`archive-order-${order.id}`}>
                                    <Archive className="mr-2 h-4 w-4" />Archive Client
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteOrder(order.id, order.client_company || order.client_name)}
                                    className="text-destructive focus:text-destructive"
                                    data-testid={`delete-order-${order.id}`}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />Delete Order
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
